import { Star, StarBorder } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Card,
    CardContent,
    IconButton,
    styled,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { HeaderFormatEnum } from "../../../components/common-table/enum/header-format";
import TableComponent, {
    ITableColumn,
} from "../../../components/common-table/table-component";
import DownloadLocations from "../../../components/download-locations/download-locations";
import { displayDateFormat } from "../../../constants";
import { downloadListLocationsAction } from "../../../features/download/redux/download.actions";
import useDebounce from "../../../hooks/use-debounce";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { trackedListColumns } from "../../../service/tracked-list-columns";
import { getListDetailsPath } from "../../../shared/component/get-list-details-path";
import RenderLink from "../../../shared/component/render-link";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { TrackedListColumns } from "../enum/tracked-list-columns";
import { ITrackedList } from "../interface/dto/tracked-lists.dto";
import {
    trackedListSetFiltersAction,
    trackedListSetPaginationAction,
    trackedListSetSortingAction,
    trackedListUntrackAction,
} from "../redux/tracked-lists.actions";
import { ITEMS_PER_PAGE_ALL } from "../selectors/filters-and-pagination";

const STAR_COLUMN = "star_column";
const DOWNLOAD_COLUMN = "download_column";

const TABLE_COLUMNS = [
    {
        title: "",
        id: STAR_COLUMN,
        sortable: false,
        width: 12,
    },
    ...trackedListColumns.map((column) => ({
        title: column.name,
        id: column.id,
        sortable: true,
        width: column.width,
    })),
    {
        title: "Download",
        id: DOWNLOAD_COLUMN,
        sortable: false,
        width: 70,
    },
];

const TrackedListsTable = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();
    const [query, setQuery] = useState<string | undefined>(undefined);
    const { trackedLists, pagination, isTrackListLoading, searchQuery } =
        useAppSelector((state) => state.trackedList.table);
    const debouncedQuery = useDebounce(query);

    useEffect(() => {
        if (query !== undefined) {
            dispatch(trackedListSetFiltersAction(debouncedQuery ?? ""));
        }
    }, [debouncedQuery, dispatch]);

    const handleRowsPerPageChange = (rows: number) => {
        dispatch(trackedListSetPaginationAction(pagination.currentPage, rows));
    };

    const handlePageChange = (page: number) => {
        dispatch(trackedListSetPaginationAction(page, pagination.itemsPerPage));
    };

    const handleColumnOrder = (
        columnIndex: number,
        column: ITableColumn,
        direction: "asc" | "desc",
    ) => {
        dispatch(
            trackedListSetSortingAction(
                column.id as TrackedListColumns,
                direction,
            ),
        );
    };

    const toggleStarredList = (rowId: number) => {
        dispatch(trackedListUntrackAction(rowId));
    };

    const getCellContent = (
        list: ITrackedList,
        columnKey: string,
        rowId: number,
    ): ReactNode => {
        if (columnKey === STAR_COLUMN) {
            const isStarred = trackedLists?.lists.some(
                (listItem) => listItem.listId === rowId,
            );
            return (
                <Tooltip title="Remove from your Tracked Lists Page">
                    <IconButton
                        onClick={() => toggleStarredList(rowId)}
                        disableFocusRipple={true}
                        disableRipple={true}
                        disableTouchRipple={true}
                    >
                        <StarIconStyle>
                            {isStarred ? <Star /> : <StarBorder />}
                        </StarIconStyle>
                    </IconButton>
                </Tooltip>
            );
        } else if (columnKey === DOWNLOAD_COLUMN) {
            return (
                <DownloadLocations
                    downloadAction={() => downloadListLocationsAction(rowId)}
                />
            );
        } else if (columnKey === TrackedListColumns.LAST_UPDATED) {
            return moment
                .utc(list[TrackedListColumns.LAST_UPDATED])
                .format(displayDateFormat);
        } else if (columnKey === TrackedListColumns.NAME) {
            return (
                <RenderLink
                    href={getListDetailsPath(
                        list[TrackedListColumns.LIST_ID].toString(),
                    )}
                >
                    {list[columnKey as keyof ITrackedList]?.toString()}
                </RenderLink>
            );
        } else {
            return list[columnKey as keyof ITrackedList]?.toString();
        }
    };

    const renderCell = (rowIndex: number, columnIndex: number): ReactNode => {
        const adjustedRowIndex =
            pagination.itemsPerPage === ITEMS_PER_PAGE_ALL
                ? rowIndex
                : rowIndex % pagination.itemsPerPage;

        const list = trackedLists?.lists?.[adjustedRowIndex];
        if (!list) return null;

        const columnKey = TABLE_COLUMNS[columnIndex].id;
        const rowId = list[TrackedListColumns.LIST_ID];
        const cellContent = getCellContent(list, columnKey, rowId);

        return <span className={className}> {cellContent} </span>;
    };

    const renderNoDataMessage = () => {
        if (query && query.trim().length > 0) {
            return `No results found for "${query}". Try different search criteria.`;
        }
    };

    if (
        trackedLists &&
        !trackedLists.total &&
        !isTrackListLoading &&
        !searchQuery?.length
    ) {
        return (
            <div className={className}>
                <Card>
                    <CardContent className="centered-content">
                        <Typography variant="body1">
                            You are not currently tracking any lists. To start
                            tracking a list you can:
                            <br />
                            Search or Select a List from the below drop-down and
                            click &quot;Track this List&quot; button.
                        </Typography>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <Box className={className}>
            <TableComponent
                columns={TABLE_COLUMNS}
                headerFormat={HeaderFormatEnum.PageSizeLeft}
                rowsPerPage={pagination.itemsPerPage}
                totalRows={trackedLists?.total ?? 0}
                currentPage={pagination.currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handlePageChange}
                onColumnOrder={handleColumnOrder}
                renderCell={renderCell}
                renderNoDataMessage={renderNoDataMessage}
                isLoading={isTrackListLoading}
                uniqueRowId={(rowIndex) => `${rowIndex}`}
                customHeader={
                    <div className="search-box-placeholder">
                        <TextField
                            variant="standard"
                            value={query}
                            onChange={({ currentTarget }) =>
                                setQuery(currentTarget.value)
                            }
                            InputProps={{
                                endAdornment: (
                                    <SearchIcon className="search-icon" />
                                ),
                            }}
                        />
                    </div>
                }
            />
        </Box>
    );
};

const StarIconStyle = styled("span")(({ theme }: IWithThemeProps) => ({
    "& svg": {
        color: theme?.palette.trackedListsTable.starIconColor,
        height: theme?.spacing(2),
    },
}));

export default styled(TrackedListsTable)(({ theme }: IWithThemeProps) => ({
    ".centered-content": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
    },
    a: {
        textDecoration: "none",
        color: theme?.palette.trackedListsTable.textColor,
    },
    ".search-box-placeholder": {
        display: "flex",
        alignItems: "center",
        marginRight: theme?.spacing(2),
    },
    ".search-icon": {
        color: theme?.palette.text.primary,
    },
}));
